import { useState } from 'react';
import { ChevronDown, ChevronUp, Copy, Eye, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/design/components';
import { cn } from '@/lib/cn';
import { ANSWER_KIND_LABEL, type AnswerKind } from '../api';
import { SurveyPreview } from './SurveyPreview';
import { type DraftQuestion } from './api';
import { type SurveyDraft } from './draft';
import { emptyQuestion } from './api';

const FIELD = 'min-h-11 w-full rounded-sm border border-muted bg-surface px-3 text-body text-ink';
const AREA = `${FIELD} py-2`;

/**
 * בונה הסקרים (מיגרציה 085).
 *
 * קודם היה אפשר להקליד שאלות שורה אחרי שורה, עם סוג תשובה אחד
 * לכל הסקר. זה החזיק סקר של שלוש שאלות, ולא סקר אמיתי שמערבב
 * דירוג, בחירה וטקסט — ובחירה בכלל לא הייתה ניתנת ליצירה, אף
 * שהמסך של העובד כבר ידע להציג אותה.
 *
 * הערת הניתוח היא המקום שבו מי שכותב את השאלה מסביר למי שיקרא
 * את התוצאות מה היא באה לבחון. בלעדיה הסקר השני נכתב מחדש על ידי
 * מישהו אחר, והמספרים מפסיקים להיות ברי השוואה.
 */
export function SurveyBuilder({
  draft,
  onChange,
  onSubmit,
  onCancel,
  saving,
  locked = false,
}: {
  draft: SurveyDraft;
  onChange: (next: SurveyDraft) => void;
  onSubmit: () => void;
  onCancel: () => void;
  saving: boolean;
  /**
   * יש תשובות: השאלות עצמן נעולות ורק המעטפת פתוחה. מגיע
   * מהשרת (has_responses), ונאכף שם — כאן זו רק התצוגה.
   */
  locked?: boolean;
}) {
  const set = <K extends keyof SurveyDraft>(key: K, value: SurveyDraft[K]) =>
    onChange({ ...draft, [key]: value });

  const setQuestion = (index: number, next: DraftQuestion) =>
    set(
      'questions',
      draft.questions.map((question, i) => (i === index ? next : question)),
    );

  const move = (index: number, by: number) => {
    const next = [...draft.questions];
    const target = index + by;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    set('questions', next);
  };

  const [previewing, setPreviewing] = useState(false);

  const ready = draft.title.trim() !== '' && draft.questions.every((q) => q.prompt.trim() !== '');

  return (
    <form
      className="grid gap-4"
      onSubmit={(event) => {
        event.preventDefault();
        if (ready) onSubmit();
      }}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="grid gap-1">
          <span className="text-body-sm text-muted">שם הסקר</span>
          <input
            value={draft.title}
            onChange={(event) => set('title', event.target.value)}
            className={FIELD}
          />
        </label>
        <label className="grid gap-1">
          <span className="text-body-sm text-muted">הסבר קצר, מה שהעובד רואה</span>
          <input
            value={draft.description}
            onChange={(event) => set('description', event.target.value)}
            className={FIELD}
          />
        </label>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="grid gap-1">
          <span className="text-body-sm text-muted">פתיחה</span>
          <input
            type="datetime-local"
            value={draft.starts_at}
            onChange={(event) => set('starts_at', event.target.value)}
            className={FIELD}
          />
          {/*
            בניית סקר ארוך לוקחת זמן, וההתראה יוצאת כשהוא נפתח.
            מועד פתיחה עתידי הוא מה שמאפשר לשמור אותו, לקרוא שוב
            מחר ולשלוח רק אז.
          */}
          <span className="text-label text-muted">ריק = נפתח עכשיו ומתריע לכולם</span>
        </label>
        <label className="grid gap-1">
          <span className="text-body-sm text-muted">סגירה</span>
          <input
            type="datetime-local"
            value={draft.ends_at}
            onChange={(event) => set('ends_at', event.target.value)}
            className={FIELD}
          />
          <span className="text-label text-muted">ריק = נשאר פתוח</span>
        </label>
      </div>

      <label className="grid gap-1">
        <span className="text-body-sm text-muted">
          איך לקרוא את הסקר הזה <span className="text-muted">למי שמנתח, לא לעובדים</span>
        </span>
        <textarea
          rows={3}
          value={draft.analysis_note}
          onChange={(event) => set('analysis_note', event.target.value)}
          placeholder="אחוז השתתפות סביר, מה הסקר לא יגיד, מה להצליב מולו"
          className={AREA}
        />
      </label>

      <label className="flex items-start gap-2 text-body">
        <input
          type="checkbox"
          checked={draft.is_anonymous}
          onChange={(event) => set('is_anonymous', event.target.checked)}
          className="mt-0.5 size-5"
        />
        <span>
          סקר אנונימי
          <span className="block text-label text-muted">
            התשובות נשמרות בלי שם. הסניף כן נשמר, וזה נאמר לעובד במסך. אי אפשר לשנות אחרי שהסקר
            נפתח.
          </span>
        </span>
      </label>

      {locked && (
        <p className="m-0 rounded-sm border border-warn bg-raised p-2 text-body-sm">
          כבר ענו על הסקר. נוסח השאלות, סוג התשובה והאפשרויות נעולים — שינוי שלהם היה הופך את מה
          שכבר נענה לחסר משמעות. שם הסקר, התאריכים, החלקים והערות הניתוח פתוחים לעריכה.
        </p>
      )}

      <div className="grid gap-3">
        <div className="flex flex-wrap items-center gap-2 border-t border-line pt-3">
          <h3 className="m-0 text-title font-semibold text-ink">
            שאלות <span className="font-normal text-muted">({draft.questions.length})</span>
          </h3>
          <span className="grow" />
          {!locked && (
            <Button
              variant="secondary"
              type="button"
              onClick={() =>
                set('questions', [
                  ...draft.questions,
                  // החלק של האחרונה נגרר הלאה: סקר נכתב חלק אחרי
                  // חלק, ולהקליד אותו שם מחדש בכל שאלה זה מס
                  emptyQuestion(draft.questions.at(-1)?.section ?? ''),
                ])
              }
            >
              <Plus size={18} strokeWidth={1.75} aria-hidden="true" />
              הוספת שאלה
            </Button>
          )}
        </div>

        {draft.questions.map((question, index) => (
          <QuestionEditor
            key={question.key}
            question={question}
            index={index}
            isFirst={index === 0}
            isLast={index === draft.questions.length - 1}
            canRemove={draft.questions.length > 1}
            locked={locked}
            onChange={(next) => setQuestion(index, next)}
            onMove={(by) => move(index, by)}
            onDuplicate={() =>
              set('questions', [
                ...draft.questions.slice(0, index + 1),
                { ...question, key: crypto.randomUUID() },
                ...draft.questions.slice(index + 1),
              ])
            }
            onRemove={() =>
              set(
                'questions',
                draft.questions.filter((_, i) => i !== index),
              )
            }
          />
        ))}
      </div>

      {/*
        סרגל דביק ולא כפתור בתחתית הטופס.

        מערכת העיצוב אומרת שבדסקטופ אין "כפתור ראשי דביק", כי אין
        לאן לגלול. סקר של עשרים שאלות שובר את ההנחה הזו: תיקון
        מילה בשאלה השלישית דרש גלילה דרך שמונה עשרה שאלות כדי
        לשמור אותו.

        דביק ולא צף: הוא נשאר בזרימה, ולכן בסוף הטופס הוא יושב
        במקומו ואינו מכסה את השאלה האחרונה. ה Panel שעוטף אותו
        מקבל overflow-visible, אחרת sticky לא עובד בתוכו בכלל.
      */}
      <div className="sticky bottom-0 z-10 -mx-4 -mb-4 flex flex-wrap items-center gap-2 border-t border-line bg-surface px-4 py-3">
        <Button variant="primary" type="submit" disabled={!ready || saving}>
          {locked ? 'שמירת השינויים' : draft.starts_at ? 'שמירה ותזמון' : 'פתיחת הסקר ושליחת התראה'}
        </Button>
        {/*
          לפני השמירה ולא אחריה: סקר שרואים רק אחרי שהוא פורסם
          כבר שלח התראה לכולם.
        */}
        <Button variant="secondary" type="button" onClick={() => setPreviewing(true)}>
          <Eye size={18} strokeWidth={1.75} aria-hidden="true" />
          תצוגה מקדימה
        </Button>
        <Button type="button" onClick={onCancel}>
          ביטול
        </Button>

        <span className="grow" />

        {/* מה חוסם את השמירה, במקום כפתור מושבת בלי הסבר */}
        <span className="text-label text-muted">
          {draft.title.trim() === ''
            ? 'חסר שם לסקר'
            : !ready
              ? 'יש שאלה בלי נוסח'
              : `${draft.questions.length} שאלות`}
        </span>
      </div>

      {previewing && <SurveyPreview draft={draft} onClose={() => setPreviewing(false)} />}
    </form>
  );
}

function QuestionEditor({
  question,
  index,
  isFirst,
  isLast,
  canRemove,
  locked,
  onChange,
  onMove,
  onDuplicate,
  onRemove,
}: {
  question: DraftQuestion;
  index: number;
  isFirst: boolean;
  isLast: boolean;
  canRemove: boolean;
  locked: boolean;
  onChange: (next: DraftQuestion) => void;
  onMove: (by: number) => void;
  onDuplicate: () => void;
  onRemove: () => void;
}) {
  const [showNote, setShowNote] = useState(question.analysis_note !== '');
  const hasOptions = question.answer_kind === 'choice' || question.answer_kind === 'multi';

  const set = <K extends keyof DraftQuestion>(key: K, value: DraftQuestion[K]) =>
    onChange({ ...question, [key]: value });

  return (
    <fieldset className="m-0 grid gap-3 rounded-md border border-line bg-raised p-3">
      <legend className="sr-only">שאלה {index + 1}</legend>

      <div className="flex flex-wrap items-start gap-2">
        <span className="mt-2.5 text-label font-medium text-muted">{index + 1}.</span>
        <input
          value={question.prompt}
          onChange={(event) => set('prompt', event.target.value)}
          readOnly={locked}
          placeholder="נוסח השאלה, כמו שהעובד יקרא אותה"
          aria-label={`נוסח שאלה ${index + 1}`}
          className={cn(`${FIELD} min-w-0 flex-1`, locked && 'bg-cream text-muted')}
        />
        <div className="flex items-center gap-1">
          <IconButton label="למעלה" disabled={isFirst || locked} onClick={() => onMove(-1)}>
            <ChevronUp size={18} strokeWidth={1.75} aria-hidden="true" />
          </IconButton>
          <IconButton label="למטה" disabled={isLast || locked} onClick={() => onMove(1)}>
            <ChevronDown size={18} strokeWidth={1.75} aria-hidden="true" />
          </IconButton>
          <IconButton label="שכפול" disabled={locked} onClick={onDuplicate}>
            <Copy size={18} strokeWidth={1.75} aria-hidden="true" />
          </IconButton>
          <IconButton label="מחיקה" disabled={!canRemove || locked} onClick={onRemove}>
            <Trash2 size={18} strokeWidth={1.75} aria-hidden="true" />
          </IconButton>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="grid gap-1">
          <span className="text-label text-muted">סוג התשובה</span>
          <select
            value={question.answer_kind}
            onChange={(event) => {
              const kind = event.target.value as AnswerKind;
              onChange({
                ...question,
                answer_kind: kind,
                // מעבר לדירוג או לטקסט מוחק את האפשרויות: השרת
                // דוחה אותן, והשארתן כאן נראית כאילו הן קיימות
                options: kind === 'choice' || kind === 'multi' ? question.options : [],
                allow_na: kind === 'scale' ? question.allow_na : false,
              });
            }}
            disabled={locked}
            className={cn(FIELD, locked && 'bg-cream text-muted')}
          >
            {(Object.keys(ANSWER_KIND_LABEL) as AnswerKind[]).map((kind) => (
              <option key={kind} value={kind}>
                {ANSWER_KIND_LABEL[kind]}
              </option>
            ))}
          </select>
        </label>

        <label className="grid gap-1">
          <span className="text-label text-muted">חלק בסקר</span>
          <input
            value={question.section}
            onChange={(event) => set('section', event.target.value)}
            placeholder="לדוגמה: סידור עבודה"
            className={FIELD}
          />
        </label>
      </div>

      {hasOptions && (
        <Options
          options={question.options}
          locked={locked}
          onChange={(options) => set('options', options)}
        />
      )}

      <div className="flex flex-wrap gap-x-4 gap-y-2">
        <label className="flex items-center gap-2 text-body-sm">
          <input
            type="checkbox"
            checked={question.is_required}
            onChange={(event) => set('is_required', event.target.checked)}
            className="size-5"
          />
          חובה לענות
        </label>

        {question.answer_kind === 'scale' && (
          <label className="flex items-center gap-2 text-body-sm">
            <input
              type="checkbox"
              checked={question.allow_na}
              onChange={(event) => set('allow_na', event.target.checked)}
              disabled={locked}
              className="size-5"
            />
            להוסיף "לא רלוונטי"
          </label>
        )}
      </div>

      {question.answer_kind === 'scale' && !question.allow_na && (
        <p className="m-0 text-label text-muted">
          בלי "לא רלוונטי", מי שלא השתמש בדבר שנשאל עליו ידרג אותו בכל זאת, והממוצע ימדוד ניחושים.
        </p>
      )}

      {showNote ? (
        <label className="grid gap-1">
          <span className="text-label text-muted">
            למה נשאלה ואיך לקרוא את התשובה <span className="text-muted">מופיע רק במסך התוצאות</span>
          </span>
          <textarea
            rows={3}
            autoFocus={question.analysis_note === ''}
            value={question.analysis_note}
            onChange={(event) => set('analysis_note', event.target.value)}
            placeholder="מה השאלה באה לבחון, מה נחשב תשובה טובה, ומה לעשות עם תשובה חריגה"
            className={AREA}
          />
        </label>
      ) : (
        <button
          type="button"
          onClick={() => setShowNote(true)}
          className="w-fit cursor-pointer border-0 bg-transparent p-0 text-label text-ink underline-offset-2"
        >
          + הוספת הסבר לניתוח
        </button>
      )}
    </fieldset>
  );
}

function Options({
  options,
  locked,
  onChange,
}: {
  options: string[];
  locked: boolean;
  onChange: (next: string[]) => void;
}) {
  const rows = options.length > 0 ? options : ['', ''];

  return (
    <div className="grid gap-2">
      <span className="text-label text-muted">אפשרויות, שתיים לפחות</span>
      {rows.map((option, index) => (
        <div key={index} className="flex items-center gap-2">
          <input
            value={option}
            onChange={(event) =>
              onChange(rows.map((row, i) => (i === index ? event.target.value : row)))
            }
            readOnly={locked}
            placeholder={`אפשרות ${index + 1}`}
            aria-label={`אפשרות ${index + 1}`}
            className={cn(`${FIELD} min-w-0 flex-1`, locked && 'bg-cream text-muted')}
          />
          <IconButton
            label={`מחיקת אפשרות ${index + 1}`}
            disabled={rows.length <= 2 || locked}
            onClick={() => onChange(rows.filter((_, i) => i !== index))}
          >
            <Trash2 size={18} strokeWidth={1.75} aria-hidden="true" />
          </IconButton>
        </div>
      ))}
      {!locked && (
        <Button variant="secondary" type="button" onClick={() => onChange([...rows, ''])}>
          <Plus size={18} strokeWidth={1.75} aria-hidden="true" />
          אפשרות נוספת
        </Button>
      )}
    </div>
  );
}

function IconButton({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'inline-flex size-11 cursor-pointer items-center justify-center rounded-sm bg-transparent text-ink',
        'disabled:cursor-not-allowed disabled:opacity-40',
      )}
    >
      {children}
    </button>
  );
}
